/**
 * Similar community card
 *
 * Displays community information and provides the ability to
 * -View community management page
 *
 * Favourite cannot be implemented at the moment as the action sets the current value for the community to refresh community management page.
 * This does not work with the creation form as it prefills the form with the current value for community in the current architecture.
 */

/**
 * React
 */
import React, { useState } from 'react';

/**
 * UI Fabric
 */
import {
  DocumentCard,
  DocumentCardTitle,
  DocumentCardType
} from '@fluentui/react/lib/DocumentCard';
import { DefaultButton, IconButton } from '@fluentui/react/lib/Button';
import { Callout } from '@fluentui/react/lib/Callout';
import { TooltipHost } from '@fluentui/react/lib/Tooltip';

interface CommunityProp {
  communityId: number;
  communityAzureId: string;
  communityType: string;
  isOwner: boolean;
  pendingRequests: number;
  totalMembers: number;
  sponsored: boolean;
  displayName: string;
  description: string;
}

interface Props {
  community: CommunityProp;
  tooltipSponsoredBadge: string;
  onSimilarCommunityClick(community: any): void;
}

const SimilarCommunityCard = (props: Props) => {
  const {
    community,
    tooltipSponsoredBadge,
    onSimilarCommunityClick
  } = props;
  const {
    communityId,
    communityAzureId,
    communityType,
    isOwner,
    pendingRequests,
    totalMembers,
    sponsored,
    displayName,
    description,
  } = community || {};
  const [sponsoredBadge, setSponsoredBadge] = useState<HTMLDivElement | null>(null);
  const [hideSponsoredCallout, setHideSponsoredCallout] = useState<boolean>(true);

  const _onClick = (ev: React.MouseEvent<any>) => {
    ev.preventDefault();
    ev.stopPropagation();
    onSimilarCommunityClick(community);
    console.log('community 1', community);
  };

  /**
   * Get truncated community description
   */
  const _getDescription = (_description, maxLen = 80) => {
    if (description && description.length > maxLen) {
      return _description.substring(0, maxLen) + '...';
    }
    return _description;
  };

  /**
   * Get card header colour based on community type
   */
  const _getColorClass = (communityType) => {
    switch (communityType) {
      case 'Open':
        return 'community-open';
      case 'Private':
        return 'community-closed';
      case 'Hidden':
        return 'community-hidden';
      default:
        return 'govTeams-blue';
    }
  };

  return (
      <DocumentCard
          type={DocumentCardType.normal}
          onClick={_onClick}
      >
        <div className={`ms-DocumentCard-details ${_getColorClass(communityType)}`}>
          {communityId && (
              <div
                  className={
                      'communityCard-header ' +
                      _getColorClass(communityType)
                  }
              >
                <div className='card-header-left'>
                  {isOwner && pendingRequests > 0 && (
                      <div className='alerts'>
                        <TooltipHost content='Pending requests'>
                          <DefaultButton
                              iconProps={{ iconName: 'Ringer' }}
                              text={pendingRequests.toString()}
                          />
                        </TooltipHost>
                      </div>
                  )}
                  {communityId > 0 && (
                      <div className='members'>
                        <TooltipHost
                            content={`This community has ${totalMembers} member(s)`}
                        >
                          <DefaultButton
                              iconProps={{ iconName: 'Contact' }}
                              text={totalMembers.toString()}
                          />
                        </TooltipHost>
                      </div>
                  )}
                </div>
                {sponsored && (
                    <React.Fragment>
                      <div
                          className='sponsoredBadge'
                          ref={(_sponsoredBadge) => {
                            setSponsoredBadge(_sponsoredBadge);
                          }}
                          onMouseOver={() => {
                            setHideSponsoredCallout(false);
                          }}
                          onMouseOut={() => {
                            setHideSponsoredCallout(true);
                          }}
                      >
                        Sponsored
                      </div>
                      <Callout
                          hidden={hideSponsoredCallout}
                          target={sponsoredBadge}
                          calloutMaxWidth={300}
                      >
                        <div className='govTeams-Callout-body'>
                          <h3>Sponsored Communities</h3>
                          <p>{tooltipSponsoredBadge}</p>
                        </div>
                      </Callout>
                    </React.Fragment>
                )}
                <div className='card-header-right'>
                  <div className='status'>
                    <TooltipHost
                        content={`This community is ${communityType}`}
                    >
                      {communityType !== 'Hidden' ? (
                          <IconButton
                              iconProps={{
                                iconName:
                                    communityType === 'Open'
                                        ? 'Unlock'
                                        : 'Lock'
                              }}
                          />
                      ) : (
                          <IconButton iconProps={{ iconName: 'Hide' }} />
                      )}
                    </TooltipHost>
                  </div>
                </div>
              </div>
          )}

          <DocumentCardTitle title={displayName} />

          {description && (
              <div className='communityCard-description'>
                {_getDescription(description)}
              </div>
          )}
        </div>
      </DocumentCard>
  );
};

export default SimilarCommunityCard;
